/**
 * THE COLUMN PROFILE — what one column of the world contains, resolved once and written at any
 * stride. See docs/node-graph-refactor-plan.md §2.2.
 *
 * It exists because `generateChunk`, `generateChunkLOD`, `generateLodColumn` and `sampleSurface`
 * were four hand-maintained copies of the same per-column pipeline, differing in stride and in
 * which subsets they ran. Every cross-LOD defect the region work recorded was a disagreement
 * between two of those copies — a thin feature point-sampled on one path and max-downsampled on
 * another, a clip that believed a different tower height from the draw. One profile makes that
 * class of bug unwritable: the clip reads the same numbers the draw writes, because they ARE the
 * same numbers.
 *
 * PREALLOCATED AND REUSED, never allocated in the loop (perf invariant 7). One per generator; a
 * generator is single-threaded (one per worker), and a profile is dead the moment its column's Y
 * loop ends.
 *
 * THE SHAPE IS NOT GENERIC, AND THAT IS DELIBERATE. Water and the tree dot are FILLS — they
 * overwrite a voxel's weight and material between two heights. The boundary wall and the coarse
 * city mass are EXTRUSIONS — a horizontal distance and a top, merged by `max`, which is the
 * no-carve rule stated numerically (voxel/surfaceBand.ts). Those are two different operations with
 * two different precedences, and collapsing them into one list would mean re-deciding per entry
 * which one it is, in the hottest loop in the project. So they are separate slots, and the
 * extrusions are a fixed-size array in precedence order.
 *
 * `bottom` and `suppress` are here before anything writes them. `bottom` is the carve seam's hook
 * (plan §3.5): a kind that removes material below the surface has to push the vertical clip down
 * with it, or it is a cavity in terrain the streamer has promised is solid rock. `suppress` is the
 * breach seam (§3.3): the bits any kind may contribute and any placer must read. Both are read by
 * code that exists today, so neither can arrive later as an afterthought that one of the callers
 * quietly forgets.
 */
import { getWeight, packVoxel } from '../../voxel/voxelData.js';
import { WEIGHT_MAX_PACKED, WEIGHT_RANGE } from '../../voxel/constants.js';
/** How many extrusions one column may carry. Two today: the coarse city mass, then the wall. */
export const MAX_COLUMN_EXTRUSIONS = 4;
/**
 * Phases, in composition order. `resolveColumn` stops after the one it is asked for, which is a
 * real fast path rather than a convenience: `sampleHeight` is the hottest function in the project
 * and must not pay for extrusions it does not read, and the coarse column CLIP must not pay for the
 * skin's slope sample.
 *
 * SKIN IS LAST because it is material only — nothing downstream reads it — which is what lets the
 * clip stop before it.
 */
export const PHASE_HEIGHT = 0;
export const PHASE_WATER = 1;
export const PHASE_EXTRUSION = 2;
export const PHASE_SKIN = 3;
/** Bits any kind may contribute, and any kind that places content must read. Plan §3.3. */
export const SUPPRESS_SCATTER = 1;
export const SUPPRESS_STRUCTURE = 2;
export const SUPPRESS_SURFACE = 4;
export class ColumnProfile {
    /** Fine voxels one sample stands for: 1 at LOD 0, `1 << lod` above it. */
    strideFine = 1;
    /** Terrain surface, fine voxels. */
    height = 0;
    /** Lowest content in the column. The clip's `minCy` source; a carve pushes it down. */
    bottom = 0;
    /** Highest content — terrain, water, a fill or an extrusion. The air-column early-out's bound. */
    top = 0;
    /** Water surface, or -Infinity when the column is dry. */
    water = -Infinity;
    waterMat = 0;
    /**
     * The ONE air sample whose weight positions the waterline, and the weight it carries. The drawn
     * surface is an interpolation between the last water sample and the first air one, and weight is
     * packed to four bits — so the crossing is solved against what the pack actually stores, not
     * against the float. Without it, measured on a sea at fine voxel 41, lods 1-5 put the sheet at
     * 10.250 / 10.214 / 10.143 / 10.250 / 10.500 m: a 0.357 m spread and a visible ledge at every
     * ring border.
     */
    crossAirFineY = Infinity;
    crossAirWeight = -0.5;
    /** Sample forced to water where the sheet is thinner than the stride, or -Infinity. */
    waterFloorFineY = -Infinity;
    /** The per-column tree speckle above MAX_SCATTER_LOD: a fill from the terrain up to this. */
    dotTop = -Infinity;
    dotMat = 0;
    /** Landform skin material for this column, hoisted out of the Y loop (it is column-constant). */
    surfaceMat = 0;
    /** The city carriageway. Material only, so it never reaches the vertical clip. */
    onRoad = false;
    /** The region-to-region path's paving for this column, or -1. Material only, like the road. */
    pathMat = -1;
    /**
     * How far (fine voxels) this column's surface stands above its lowest neighbour, when paved —
     * by a path OR a city road. The paving band is a VERTICAL depth test, but on a slope the visible
     * face between two columns is a riser drawn from the taller column's deeper voxels — deeper than
     * the band, so risers showed strata between paved treads. The writer extends both paving bands
     * by this (surfaceBand.ts's thickness rule — measure against the surface's own normal —
     * translated to a heightfield).
     */
    pavingDropFine = 0;
    /** Contributed bits; nothing writes them yet. See SUPPRESS_*. */
    suppress = 0;
    /** Extrusions in precedence order — signed horizontal distance, top, material — merged by max. */
    exCount = 0;
    exDist = new Float64Array(MAX_COLUMN_EXTRUSIONS);
    exTop = new Float64Array(MAX_COLUMN_EXTRUSIONS);
    exMat = new Int32Array(MAX_COLUMN_EXTRUSIONS);
    /** Wipe to "bare column at this stride". Cheaper than allocating, and it cannot forget a field. */
    reset(strideFine, height) {
        this.strideFine = strideFine;
        this.height = height;
        this.bottom = height;
        this.top = height;
        this.water = -Infinity;
        this.waterMat = 0;
        this.crossAirFineY = Infinity;
        this.crossAirWeight = -0.5;
        this.waterFloorFineY = -Infinity;
        this.dotTop = -Infinity;
        this.dotMat = 0;
        this.surfaceMat = 0;
        this.onRoad = false;
        this.pathMat = -1;
        this.pavingDropFine = 0;
        this.suppress = 0;
        this.exCount = 0;
    }
    /** Add an extrusion. Ignored past the slot count, which MAX_COLUMN_EXTRUSIONS is sized to prevent. */
    addExtrusion(distFine, top, material) {
        if (this.exCount >= MAX_COLUMN_EXTRUSIONS)
            return;
        const i = this.exCount++;
        this.exDist[i] = distFine;
        this.exTop[i] = top;
        this.exMat[i] = material;
        if (top > this.top)
            this.top = top;
    }
    raiseTop(y) {
        if (y > this.top)
            this.top = y;
    }
}
/**
 * Solve the waterline against the weight the pack actually stores, and record it on the profile.
 *
 * Quantise the water sample first, then choose the AIR sample above it so that the QUANTISED pair
 * interpolates to the water level. The air voxel is free to carry it — a negative weight is OUTSIDE
 * for every mesh type, so nothing new is drawn, only the crossing between the two moves. Everything
 * below the surface keeps its physical depth, which is what waterLodConsistency reads.
 *
 * AT STRIDE 1 THIS REDUCES TO THE OLD SATURATED -0.5, exactly, whenever the water level sits on a
 * sample — which the sea always does (`seaLevel + 1`, both integers). It was rivers that made a
 * level fractional, and their return is the first time this path can differ at LOD 0. It will be
 * RIGHT when it does: a fractional level is precisely the case the quantisation exists for.
 */
export function solveWaterCrossing(p) {
    const { water, height, strideFine } = p;
    if (water === -Infinity)
        return;
    const topWaterFineY = Math.floor(water / strideFine) * strideFine; // last sample <= water
    if (topWaterFineY <= height) {
        // A sheet no sample lands in: keep the top one under the level, or the sea vanishes at lod 6.
        if (strideFine === 1 || water <= height)
            return;
        p.waterFloorFineY = topWaterFineY;
    }
    const frac = (water - topWaterFineY) / strideFine; // in [0, 1)
    p.crossAirFineY = topWaterFineY + strideFine;
    if (frac <= 0) {
        p.crossAirWeight = -0.5;
        return;
    } // the level is ON a sample
    const packedBelow = getWeight(packVoxel(Math.min(0.5, frac * 0.5), 0, 0));
    // Bounded by the pack's first NEGATIVE level: as frac -> 1 the ideal air weight -> 0-, and a value
    // that close to zero rounds UP to the first positive level, which would read as inside for both
    // the liquid and the solid pass — phantom terrain, not just a moved crossing.
    p.crossAirWeight = Math.max(-0.5, Math.min(-WEIGHT_RANGE / (2 * WEIGHT_MAX_PACKED), -packedBelow * (1 - frac) / frac));
}
/** The weight an air sample above the water carries, so the crossing is symmetric at every stride. */
export function airAboveWaterWeight(p, fineY) {
    if (fineY === p.crossAirFineY)
        return p.crossAirWeight;
    // Not coarseWeight: this must not clamp UPWARD. The value is always negative here, and the pack's
    // first positive level would read as inside.
    return Math.max(-0.5, (p.water - fineY) / p.strideFine * 0.5);
}
//# sourceMappingURL=ColumnProfile.js.map